import { api } from './api';
import type {
  AssignCandidateInput,
  AuditLogQueryParams,
  AuditLogResponse,
  CandidateAssignment,
  CandidateAssignmentCreated,
  CandidateDetail,
  CandidateFileGroups,
  CandidateFileType,
  CandidateFile,
  ConsentEvent,
  ConsentTimeline,
  GrantOfflineConsentInput,
  RequestConsentInput,
  RevokeConsentInput,
  CandidateInterview,
  CandidateNote,
  CandidateNotesQueryParams,
  CandidateNotesResponse,
  CandidateQueryParams,
  CandidateSkillDetail,
  CandidateStatus,
  CandidateTag,
  CandidatesResponse,
  CreateCandidateInput,
  CreateCandidateNoteInput,
  CreateInterviewInput,
  UpdateCandidateNoteInput,
  DuplicateCheckInput,
  DuplicateCheckResponse,
  FromCvInput,
  InterviewScope,
  ParseCvResponse,
  ParseJob,
  UpdateAssignmentInput,
  UpdateCandidateInput,
  UpdateInterviewInput,
} from '@/types/candidates';

export async function getCandidates(
  params: CandidateQueryParams = {}
): Promise<CandidatesResponse> {
  const { data } = await api.get<CandidatesResponse>('/recruitment/candidates', { params });
  return data;
}

export async function getCandidate(id: string): Promise<CandidateDetail> {
  const { data } = await api.get<CandidateDetail>(`/recruitment/candidates/${id}`);
  return data;
}

export async function getCandidateLocations(): Promise<string[]> {
  const { data } = await api.get<string[]>('/recruitment/candidates/locations');
  return data;
}

export async function getCandidateTags(): Promise<string[]> {
  const { data } = await api.get<string[]>('/recruitment/candidates/tags');
  return data;
}

export async function createCandidate(input: CreateCandidateInput): Promise<CandidateDetail> {
  const { data } = await api.post<CandidateDetail>('/recruitment/candidates', input);
  return data;
}

export async function checkCandidateDuplicates(
  input: DuplicateCheckInput,
  signal?: AbortSignal,
): Promise<DuplicateCheckResponse> {
  const { data } = await api.post<DuplicateCheckResponse>(
    '/recruitment/candidates/check-duplicate',
    input,
    { signal },
  );
  return data;
}

export async function assignCandidate(
  id: string,
  input: AssignCandidateInput
): Promise<CandidateAssignmentCreated> {
  const { data } = await api.post<CandidateAssignmentCreated>(
    `/recruitment/candidates/${id}/assignments`,
    input
  );
  return data;
}

export async function getCandidateAssignments(id: string): Promise<CandidateAssignment[]> {
  const { data } = await api.get<CandidateAssignment[]>(
    `/recruitment/candidates/${id}/assignments`
  );
  return data;
}

export async function updateCandidateAssignment(
  candidateId: string,
  assignmentId: string,
  input: UpdateAssignmentInput
): Promise<CandidateAssignment> {
  const { data } = await api.patch<CandidateAssignment>(
    `/recruitment/candidates/${candidateId}/assignments/${assignmentId}`,
    input
  );
  return data;
}

export async function archiveCandidate(id: string): Promise<{ id: string; status: string }> {
  const { data } = await api.delete<{ id: string; status: string }>(
    `/recruitment/candidates/${id}`
  );
  return data;
}

export async function hardDeleteCandidate(
  id: string
): Promise<{ id: string; deleted: true; fileCount: number }> {
  const { data } = await api.delete<{ id: string; deleted: true; fileCount: number }>(
    `/recruitment/candidates/${id}/hard-delete`
  );
  return data;
}

export async function updateCandidate(
  id: string,
  input: UpdateCandidateInput
): Promise<CandidateDetail> {
  const { data } = await api.patch<CandidateDetail>(`/recruitment/candidates/${id}`, input);
  return data;
}

export async function updateCandidateStatus(
  id: string,
  status: CandidateStatus
): Promise<CandidateDetail> {
  return updateCandidate(id, { status });
}

export async function addCandidateSkill(
  id: string,
  name: string
): Promise<CandidateSkillDetail> {
  const { data } = await api.post<CandidateSkillDetail>(
    `/recruitment/candidates/${id}/skills`,
    { name }
  );
  return data;
}

export async function setCandidateSkillVerification(
  id: string,
  skillLinkId: string,
  verified: boolean
): Promise<CandidateSkillDetail> {
  const { data } = await api.patch<CandidateSkillDetail>(
    `/recruitment/candidates/${id}/skills/${skillLinkId}`,
    { verified }
  );
  return data;
}

export async function removeCandidateSkill(id: string, skillLinkId: string): Promise<void> {
  await api.delete(`/recruitment/candidates/${id}/skills/${skillLinkId}`);
}

export interface VacancyMatchedSkill {
  skillLinkId: string;
  skillId: string;
  name: string;
  verified: boolean;
}

export interface VacancyMissingSkill {
  skillId: string;
  name: string;
}

export interface VacancyMatchResponse {
  vacancy: { id: string; title: string };
  matchedSkills: VacancyMatchedSkill[];
  missingSkills: VacancyMissingSkill[];
}

export async function getCandidateVacancyMatch(
  candidateId: string,
  vacancyId: string,
): Promise<VacancyMatchResponse> {
  const { data } = await api.get<VacancyMatchResponse>(
    `/recruitment/candidates/${candidateId}/vacancy-match`,
    { params: { vacancyId } },
  );
  return data;
}

export async function addCandidateSkillFromVacancy(
  candidateId: string,
  skillId: string,
  vacancyId: string,
): Promise<CandidateSkillDetail> {
  const { data } = await api.post<CandidateSkillDetail>(
    `/recruitment/candidates/${candidateId}/skills/from-vacancy`,
    { skillId, vacancyId },
  );
  return data;
}

export async function addCandidateTag(id: string, name: string): Promise<CandidateTag> {
  const { data } = await api.post<CandidateTag>(`/recruitment/candidates/${id}/tags`, { name });
  return data;
}

export async function removeCandidateTag(id: string, name: string): Promise<void> {
  await api.delete(`/recruitment/candidates/${id}/tags/${encodeURIComponent(name)}`);
}

export async function getCandidateNotes(
  id: string,
  params: CandidateNotesQueryParams = {}
): Promise<CandidateNotesResponse> {
  const query: Record<string, string | number> = {};
  if (params.limit) query.limit = params.limit;
  if (params.cursor) query.cursor = params.cursor;

  const { data } = await api.get<CandidateNotesResponse>(
    `/recruitment/candidates/${id}/notes`,
    { params: query }
  );
  return data;
}

export async function createCandidateNote(
  id: string,
  input: CreateCandidateNoteInput
): Promise<CandidateNote> {
  const { data } = await api.post<CandidateNote>(
    `/recruitment/candidates/${id}/notes`,
    input
  );
  return data;
}

export async function updateCandidateNote(
  id: string,
  noteId: string,
  input: UpdateCandidateNoteInput
): Promise<CandidateNote> {
  const { data } = await api.patch<CandidateNote>(
    `/recruitment/candidates/${id}/notes/${noteId}`,
    input
  );
  return data;
}

export async function deleteCandidateNote(id: string, noteId: string): Promise<void> {
  await api.delete(`/recruitment/candidates/${id}/notes/${noteId}`);
}

export async function getCandidateInterviews(
  id: string,
  params: { scope?: InterviewScope } = {}
): Promise<CandidateInterview[]> {
  const query: Record<string, string> = {};
  if (params.scope) query.scope = params.scope;
  const { data } = await api.get<CandidateInterview[]>(
    `/recruitment/candidates/${id}/interviews`,
    { params: query }
  );
  return data;
}

export async function createCandidateInterview(
  id: string,
  input: CreateInterviewInput
): Promise<CandidateInterview> {
  const { data } = await api.post<CandidateInterview>(
    `/recruitment/candidates/${id}/interviews`,
    input
  );
  return data;
}

export async function updateCandidateInterview(
  candidateId: string,
  interviewId: string,
  input: UpdateInterviewInput
): Promise<CandidateInterview> {
  const { data } = await api.patch<CandidateInterview>(
    `/recruitment/candidates/${candidateId}/interviews/${interviewId}`,
    input
  );
  return data;
}

export async function cancelCandidateInterview(
  candidateId: string,
  interviewId: string
): Promise<CandidateInterview> {
  const { data } = await api.delete<CandidateInterview>(
    `/recruitment/candidates/${candidateId}/interviews/${interviewId}`
  );
  return data;
}

export async function getCandidateFiles(
  id: string,
  params: { fileType?: CandidateFileType } = {}
): Promise<CandidateFileGroups> {
  const query: Record<string, string> = {};
  if (params.fileType) query.fileType = params.fileType;
  const { data } = await api.get<CandidateFileGroups>(
    `/recruitment/candidates/${id}/files`,
    { params: query }
  );
  return data;
}

export async function uploadCandidateFile(
  id: string,
  file: File,
  fileType: CandidateFileType
): Promise<CandidateFile> {
  const form = new FormData();
  form.append('file', file);
  form.append('fileType', fileType);
  const { data } = await api.post<CandidateFile>(
    `/recruitment/candidates/${id}/files`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return data;
}

export async function deleteCandidateFile(
  candidateId: string,
  fileId: string
): Promise<void> {
  await api.delete(`/recruitment/candidates/${candidateId}/files/${fileId}`);
}

export function candidateFileDownloadUrl(candidateId: string, fileId: string): string {
  return `/api/v1/recruitment/candidates/${candidateId}/files/${fileId}/download`;
}

export async function getCandidateConsentTimeline(id: string): Promise<ConsentTimeline> {
  const { data } = await api.get<ConsentTimeline>(
    `/recruitment/candidates/${id}/consent-events`
  );
  return data;
}

export async function requestCandidateConsent(
  id: string,
  input: RequestConsentInput = {}
): Promise<ConsentEvent> {
  const { data } = await api.post<ConsentEvent>(
    `/recruitment/candidates/${id}/consent-events/request`,
    input
  );
  return data;
}

export async function grantOfflineCandidateConsent(
  id: string,
  input: GrantOfflineConsentInput
): Promise<ConsentEvent> {
  const { data } = await api.post<ConsentEvent>(
    `/recruitment/candidates/${id}/consent-events/grant-offline`,
    input
  );
  return data;
}

export async function revokeCandidateConsent(
  id: string,
  input: RevokeConsentInput
): Promise<ConsentEvent> {
  const { data } = await api.post<ConsentEvent>(
    `/recruitment/candidates/${id}/consent-events/revoke`,
    input
  );
  return data;
}

export async function anonymiseCandidate(
  id: string
): Promise<{ ok: boolean; filesDeleted: number }> {
  const { data } = await api.post<{ ok: boolean; filesDeleted: number }>(
    `/recruitment/candidates/${id}/anonymise`
  );
  return data;
}

export async function getCandidateAuditLog(
  id: string,
  params: AuditLogQueryParams = {}
): Promise<AuditLogResponse> {
  const query: Record<string, string | number> = {};
  if (params.limit) query.limit = params.limit;
  if (params.cursor) query.cursor = params.cursor;
  if (params.types && params.types.length > 0) query.types = params.types.join(',');

  const { data } = await api.get<AuditLogResponse>(
    `/recruitment/candidates/${id}/audit-log`,
    { params: query }
  );
  return data;
}

export async function parseCvForCandidate(
  candidateId: string,
  file: File,
): Promise<ParseCvResponse> {
  const form = new FormData();
  form.append('file', file);
  const { data } = await api.post<ParseCvResponse>(
    `/recruitment/candidates/${candidateId}/parse-cv`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data;
}

export async function parseCvStandalone(file: File): Promise<ParseCvResponse> {
  const form = new FormData();
  form.append('file', file);
  const { data } = await api.post<ParseCvResponse>(
    '/recruitment/candidates/parse-cv',
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data;
}

export async function getParseJob(id: string): Promise<ParseJob> {
  const { data } = await api.get<ParseJob>(`/recruitment/parse-jobs/${id}`);
  return data;
}

export async function commitFromCv(input: FromCvInput): Promise<CandidateDetail> {
  const { data } = await api.post<CandidateDetail>(
    '/recruitment/candidates/from-cv',
    input,
  );
  return data;
}
